// Domínios, com o verificador de DNS.
//
// O PROMPTS.md chama o verificador de peça central da fase, e a razão é de
// SUPORTE: DNS mal configurado é a causa mais comum de "meu e-mail não
// chega", e diagnosticar isso por telefone consome horas. A tela precisa
// dizer QUAL dos cinco está errado e QUAL valor deveria estar lá — senão ela
// só troca uma pergunta por outra.

import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { dominios, dkimDoDominio, revendas } from "../api/admin";
import { ErroDaAPI, explicar } from "../api/problema";
import type { DiagnosticoDns, Dominio } from "../api/tipos";
import { Icone } from "../ui/Icone";
import { useSessao } from "../sessao/contexto";
import {
  Botao,
  Cabecalho,
  Celula,
  ConfirmarRemocao,
  Estado,
  Legenda,
  Linha,
  Paginacao,
  Selo,
  Tabela,
  usePagina,
} from "./comuns";
import { criarVez } from "./vez";

// As bolinhas de todas as linhas se preenchem sozinhas, de quatro em quatro.
// Ver vez.ts: cinquenta conferências de uma vez seriam quinhentas consultas
// de DNS no mesmo segundo.
const naVez = criarVez(4);

export function Dominios() {
  const { podeVer } = useSessao();
  const fila = useQueryClient();
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState("");
  const [revenda, setRevenda] = useState("");
  const [erro, setErro] = useState("");
  const [aberto, setAberto] = useState<string | null>(null);
  const [aRemover, setARemover] = useState<Dominio | null>(null);

  const [pagina, setPagina] = usePagina();
  const lista = useQuery({
    queryKey: ["p", "dominios", "lista", pagina],
    queryFn: () => dominios.listar({ pagina, por_pagina: 50 }),
  });

  // O super admin precisa dizer DE QUEM é o domínio; para uma revenda isso é
  // implícito, e a API recusa a criação sem esse campo. O formulário pedia só
  // o nome e o botão simplesmente não funcionava.
  const asRevendas = useQuery({
    queryKey: ["p", "revendas", "para-escolher"],
    queryFn: () => revendas.listar({ por_pagina: 200 }),
    enabled: podeVer("super_admin"),
  });

  // O AVISO QUE NÃO PRECISA SER PROCURADO.
  //
  // A conferência de DNS é sob demanda, domínio por domínio — então o estado
  // "DNS já aponta para cá e falta emitir o certificado" só apareceria para
  // quem já desconfiasse dele. Foi assim que a apollo ficou três dias com o
  // cadeado quebrado, até o cliente avisar.
  const pendentes = useQuery({
    queryKey: ["p", "dominios", "pendencias-ssl"],
    queryFn: dominios.pendenciasSSL,
    // O servidor já guarda por 5 minutos; aqui é só para trocar de página do
    // painel não disparar a varredura de novo.
    staleTime: 60_000,
  });

  const remover = useMutation({
    mutationFn: (d: Dominio) => dominios.remover(d.uuid!, d.nome!),
    onSuccess: () => {
      setARemover(null);
      void fila.invalidateQueries({ queryKey: ["p"] });
    },
  });

  const criar = useMutation({
    mutationFn: () =>
      dominios.criar({
        nome: nome.trim(),
        ...(revenda ? { revenda_uuid: revenda } : {}),
      } as never),
    onSuccess: () => {
      setNome("");
      setErro("");
      setCriando(false);
      void fila.invalidateQueries({ queryKey: ["p"] });
    },
    onError: (e) => {
      const doCampo = e instanceof ErroDaAPI ? Object.values(e.porCampo)[0]?.[0] : null;
      setErro(doCampo ?? explicar(e));
    },
  });

  return (
    <>
      <Cabecalho
        titulo="Domínios"
        descricao="O DNS é conferido ao vivo, contra os resolvedores públicos."
        acao={
          podeVer("revenda") ? (
            <Botao icone="Plus" aoClicar={() => setCriando((c) => !c)}>
              Adicionar domínio
            </Botao>
          ) : undefined
        }
      />

      {(pendentes.data?.length ?? 0) > 0 && (
        <div className="mb-5 rounded-xl border border-perigo/40 bg-perigo/10 p-4">
          <p className="text-sm font-medium text-tinta">
            {pendentes.data!.length === 1
              ? "1 domínio está sem certificado SSL"
              : `${pendentes.data!.length} domínios estão sem certificado SSL`}
          </p>
          <p className="mt-1 text-xs text-tinta-fraca">
            O DNS já aponta para cá, então quem abrir o webmail desses clientes vê
            erro de segurança no navegador.
          </p>
          <ul className="mt-3 space-y-2">
            {pendentes.data!.map((p) => (
              <li key={p.uuid} className="text-sm">
                <span className="font-medium text-tinta">{p.dominio}</span>
                {/* O COMANDO INTEIRO, pronto para copiar. Dizer "emita o
                    certificado" sem dizer como só adia a pergunta. */}
                <code className="mt-1 block overflow-x-auto rounded-lg bg-white px-3 py-2 text-xs text-tinta">
                  make cliente-webmail DOMINIO={p.dominio}
                </code>
              </li>
            ))}
          </ul>
        </div>
      )}

      {criando && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            criar.mutate();
          }}
          className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-linha bg-fundo p-4"
        >
          <input
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="empresa.com.br"
            aria-label="Nome do domínio"
            autoFocus
            className="min-w-[220px] flex-1 rounded-lg border border-linha px-3 py-2 text-sm"
          />
          {podeVer("super_admin") && (
            <select
              value={revenda}
              onChange={(e) => setRevenda(e.target.value)}
              aria-label="Revenda dona do domínio"
              className="min-w-[180px] rounded-lg border border-linha px-3 py-2 text-sm"
            >
              <option value="">De qual revenda?</option>
              {asRevendas.data?.itens?.map((r) => (
                <option key={r.uuid} value={r.uuid}>
                  {r.nome}
                </option>
              ))}
            </select>
          )}
          <Botao tipo="submit" ocupado={criar.isPending}>
            Criar
          </Botao>
          <Botao tom="neutro" aoClicar={() => setCriando(false)}>
            Cancelar
          </Botao>
          {erro && <p className="w-full text-sm text-perigo">{erro}</p>}
        </form>
      )}

      <Estado consulta={lista} vazio={lista.data?.itens?.length === 0}>
        <Tabela
          colunas={[
            "Domínio",
            "Revenda",
            "Limite de caixas",
            "MX",
            "SPF",
            "DKIM",
            "DMARC",
            "PTR",
            // Os nomes próprios do cliente. Entram como colunas separadas, e
            // não resumidos num só, porque são respostas a perguntas
            // diferentes: `mail.` é por onde o Outlook se conecta e `webmail.`
            // é por onde o navegador entra.
            "mail.",
            "webmail.",
            // FALTAVA, e isso desalinhava a tabela inteira.
            //
            // A linha desenha uma célula para cada item de EXAMES, que são
            // OITO — o certificado é o oitavo. O cabeçalho só tinha sete
            // nomes de exame, então o "" do fim ficava em cima do
            // certificado e a coluna de ações não tinha cabeçalho nenhum:
            // doze células de corpo contra onze de cabeçalho. O navegador
            // não reclama, ele só estica a última.
            //
            // Achado conferindo o apontamento "colunas de DNS sem estado" da
            // revisão de UX: a coluna sem estado mais visível era uma que
            // nem dizia o que era.
            "SSL",
            "",
          ]}
        >
          {lista.data?.itens?.map((d) => (
            <LinhaDoDominio
              key={d.uuid}
              dominio={d}
              aberto={aberto === d.uuid}
              aoAbrir={() => setAberto(aberto === d.uuid ? null : (d.uuid ?? null))}
              // REVENDA PARA CIMA, como a rota (`x-acesso: revenda`) — 03/11/2026.
              // Era só super admin, e a revenda não tinha como tirar um
              // domínio que ela mesma criou (a mokwa, com o `radik.com`). A
              // proteção não é esconder o botão: a API recusa domínio alheio
              // (404), domínio com caixa ou encaminhador (409) e exige o
              // nome digitado.
              aoRemover={podeVer("revenda") ? () => setARemover(d) : undefined}
            />
          ))}
        </Tabela>
      </Estado>
      {/* O QUE OS SÍMBOLOS QUEREM DIZER.
          A outra metade do apontamento "colunas de DNS sem estado": ✓ e ⚠
          sozinhos não se explicam, e a distinção que mais importa é entre
          "ausente e opcional" e "ausente e é problema" — as duas apareciam
          como falta de algo. */}
      <Legenda
        itens={[
          {
            marca: <Icone nome="CheckCircle2" tamanho={14} className="text-acao" />,
            diz: "publicado e batendo",
          },
          {
            marca: <Icone nome="AlertTriangle" tamanho={14} className="text-aviso" />,
            diz: "falta ou aponta para outro lugar",
          },
          { marca: <span className="text-tinta-fraca">—</span>, diz: "opcional, e não está lá" },
          {
            marca: <span className="h-2 w-2 animate-pulse rounded-full bg-linha" />,
            diz: "conferindo",
          },
          {
            marca: (
              <span className="flex h-4 w-4 items-center justify-center rounded-full border border-dashed border-linha text-[9px] text-tinta-fraca">
                ?
              </span>
            ),
            diz: "não deu para conferir — clique para tentar de novo",
          },
        ]}
      />

      <Paginacao pagina={pagina} dados={lista.data} aoMudar={setPagina} nome="domínios" />

      {aRemover && (
        <ConfirmarRemocao
          titulo="Remover domínio"
          frase={aRemover.nome!}
          ocupado={remover.isPending}
          erro={
            remover.error instanceof ErroDaAPI
              ? remover.error.mensagem
              : remover.error
                ? explicar(remover.error)
                : undefined
          }
          aoCancelar={() => setARemover(null)}
          aoConfirmar={() => remover.mutate(aRemover)}
          oQueAcontece={
            <>
              <p>
                Tira <strong>{aRemover.nome}</strong> da plataforma: ele deixa de
                receber e de enviar no mesmo instante.
              </p>
              <p className="text-tinta-fraca">
                O servidor RECUSA enquanto houver caixa ou alias no domínio —
                não há remoção em cascata, de propósito. A chave DKIM também
                não é apagada: ela fica guardada, porque o TXT publicado no DNS
                do cliente continua apontando para ela, e gerar outra depois
                faria toda mensagem do domínio falhar em silêncio.
              </p>
            </>
          }
        />
      )}
    </>
  );
}

function LinhaDoDominio({
  dominio,
  aberto,
  aoAbrir,
  aoRemover,
}: {
  dominio: Dominio;
  aberto: boolean;
  aoAbrir: () => void;
  /** Ausente para quem não é super admin: a linha não mostra o botão. */
  aoRemover?: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [antispam, setAntispam] = useState(false);
  const { podeVer } = useSessao();

  // O DNS DE TODAS AS LINHAS É CONFERIDO AO ABRIR A TELA (02/11/2026).
  //
  // Antes só a linha ABERTA consultava, e a tela nascia com oito "?" em cada
  // domínio — o Jhou abriu o painel para saber quais clientes estão ligados
  // direito e a tela não respondia a pergunta. O motivo de ser sob demanda
  // era não fazer dezenas de consultas numa requisição só; aqui cada linha
  // faz a SUA, de quatro em quatro (`naVez`), e uma página tem no máximo 50.
  //
  // A chave fica FORA do prefixo "p" de propósito: salvar um domínio
  // invalida ["p"], e isso faria toda edição de cota reconferir o DNS da
  // página inteira. DNS é dado de fora; editar o domínio não o muda.
  const dns = useQuery({
    queryKey: ["dns", dominio.uuid],
    queryFn: ({ signal }) => naVez(() => dominios.dns(dominio.uuid!), signal),
    staleTime: 5 * 60_000,
  });

  return (
    <>
      <Linha>
        <Celula>
          <span className="text-tinta">{dominio.nome}</span>
          {/* À vista na lista, e não só dentro da edição: é uma decisão de
              risco do domínio, e quem olha a lista precisa saber que ela foi
              tomada. */}
          {dominio.senha_simples && (
            <span
              className="ml-2 rounded-full border border-aviso/60 bg-aviso/10 px-2 py-0.5 text-xs text-tinta-media"
              title="As caixas deste domínio aceitam senha de 4 caracteres"
            >
              senha simples
            </span>
          )}
        </Celula>
        <Celula>{dominio.revenda_nome ?? "—"}</Celula>
        <Celula>{dominio.max_caixas ? `até ${dominio.max_caixas}` : "sem limite"}</Celula>
        {EXAMES.map((k) => {
          const c = exameDe(dns.data, k);
          return (
            <Celula key={k}>
              {c ? (
                /* A bolinha é o atalho para o detalhe: quem vê um amarelo
                   quer saber QUAL valor falta, e é lá que ele está. */
                <button
                  type="button"
                  onClick={aoAbrir}
                  title={`${NOMES[k]}: ${comoLer(c)}. Clique para ver o detalhe.`}
                  aria-label={`${NOMES[k]} de ${dominio.nome}: ${comoLer(c)}`}
                  className="flex h-5 w-5 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-acao"
                >
                  <Marca situacao={c.situacao} opcional={c.opcional} />
                </button>
              ) : dns.isError ? (
                /* NÃO CONFERIDO É UM ESTADO, e precisa aparecer como um —
                   apontamento "colunas de DNS sem estado" da revisão de UX
                   de 30/10/2026. Agora só sobra para quando a consulta
                   FALHOU, e aí a célula é o botão de tentar de novo. */
                <button
                  type="button"
                  onClick={() => void dns.refetch()}
                  title={`${NOMES[k]} — não deu para conferir agora. Clique para tentar de novo.`}
                  aria-label={`Conferir de novo o ${NOMES[k]} de ${dominio.nome}`}
                  className="flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-linha text-[10px] text-tinta-fraca hover:border-acao hover:text-acao"
                >
                  ?
                </button>
              ) : (
                <span
                  role="img"
                  title={`${NOMES[k]} — conferindo…`}
                  aria-label={`conferindo ${NOMES[k]} de ${dominio.nome}`}
                  className="flex h-5 w-5 items-center justify-center"
                >
                  <span className="h-2 w-2 animate-pulse rounded-full bg-linha" />
                </span>
              )}
            </Celula>
          );
        })}
        <Celula>
          {/* AÇÕES COMO BOTÃO, NÃO COMO LINK SUBLINHADO DE 12px.
              Eram quatro palavras sublinhadas lado a lado, com ~14px de
              altura de alvo — a revisão de UX apontou, e no celular isso é
              "remover" a um dedo de distância de "editar". Pastilha com
              borda tem o alvo inteiro clicável, estado de foco visível, e
              separa o que é destrutivo pela cor em vez de pela ordem.

              `aria-expanded` porque três destes ABREM uma gaveta logo
              abaixo, e "fechar" sozinho não diz o que está aberto. */}
          <div className="flex flex-wrap justify-end gap-1.5">
            <AcaoDaLinha
              aoClicar={aoAbrir}
              expandido={aberto}
              rotulo={`${aberto ? "Fechar o" : "Ver o"} DNS de ${dominio.nome}`}
            >
              {aberto ? "fechar" : "ver DNS"}
            </AcaoDaLinha>
            <AcaoDaLinha
              aoClicar={() => setEditando((e) => !e)}
              expandido={editando}
              rotulo={`${editando ? "Fechar a edição de" : "Editar"} ${dominio.nome}`}
            >
              {editando ? "fechar" : "editar"}
            </AcaoDaLinha>
            {/* Só revenda para cima. Esconder não é a proteção — a rota
                recusa de qualquer jeito —, mas mostrar um botão que sempre
                dá 403 seria uma forma ruim de informar. */}
            {podeVer("revenda") && (
              <AcaoDaLinha
                aoClicar={() => setAntispam((a) => !a)}
                expandido={antispam}
                rotulo={`${antispam ? "Fechar o" : "Abrir o"} antispam de ${dominio.nome}`}
              >
                {antispam ? "fechar" : "antispam"}
              </AcaoDaLinha>
            )}
            {aoRemover && (
              <AcaoDaLinha
                aoClicar={aoRemover}
                tom="perigo"
                rotulo={`Remover o domínio ${dominio.nome}`}
              >
                remover
              </AcaoDaLinha>
            )}
          </div>
        </Celula>
      </Linha>

      {aberto && (
        <tr>
          <td colSpan={12} className="bg-fundo px-4 py-4">
            <Diagnostico uuid={dominio.uuid!} consulta={dns} />
          </td>
        </tr>
      )}

      {editando && (
        <tr>
          <td colSpan={12} className="bg-fundo px-4 py-4">
            <FormaDoDominio dominio={dominio} aoFechar={() => setEditando(false)} />
          </td>
        </tr>
      )}

      {antispam && (
        <tr>
          <td colSpan={12} className="bg-fundo px-4 py-4">
            <AntispamDoDominio uuid={dominio.uuid!} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Editar o que a plataforma cobra e limita no domínio.
 *
 * DEFEITO REAL: a tela criava, listava, conferia DNS e removia — e nada mais.
 * A API aceita os quatro campos abaixo por PATCH desde a fase 3.
 *
 * A `cota_padrao_caixa_bytes` é a que mais faltava: é o número que decide a
 * "cota herdada do domínio" que a tela de Caixas mostra. Via-se a herança e
 * não havia onde mudá-la.
 */
function FormaDoDominio({ dominio, aoFechar }: { dominio: Dominio; aoFechar: () => void }) {
  const fila = useQueryClient();
  type Situacao = NonNullable<Dominio["status"]>;
  const [campos, setCampos] = useState({
    cota_gb: emGB(dominio.cota_bytes),
    padrao_gb: emGB(dominio.cota_padrao_caixa_bytes),
    max_caixas: String(dominio.max_caixas ?? 0),
    status: (dominio.status ?? "ativo") as Situacao,
    senha_simples: dominio.senha_simples ?? false,
  });

  const salvar = useMutation({
    mutationFn: () =>
      dominios.alterar(dominio.uuid!, {
        cota_bytes: emBytes(campos.cota_gb),
        cota_padrao_caixa_bytes: emBytes(campos.padrao_gb),
        max_caixas: inteiro(campos.max_caixas),
        status: campos.status,
        senha_simples: campos.senha_simples,
      } as never),
    onSuccess: () => {
      aoFechar();
      void fila.invalidateQueries({ queryKey: ["p"] });
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        salvar.mutate();
      }}
      className="grid gap-3 sm:grid-cols-4"
    >
      <CampoDoDominio
        rotulo="Cota do domínio (GB)"
        valor={campos.cota_gb}
        aoMudar={(v) => setCampos((c) => ({ ...c, cota_gb: v }))}
        dica="Soma de todas as caixas. 0 = sem teto."
      />
      <CampoDoDominio
        rotulo="Cota padrão por caixa (GB)"
        valor={campos.padrao_gb}
        aoMudar={(v) => setCampos((c) => ({ ...c, padrao_gb: v }))}
        dica="O que uma caixa SEM cota própria herda."
      />
      <CampoDoDominio
        rotulo="Limite de caixas"
        valor={campos.max_caixas}
        aoMudar={(v) => setCampos((c) => ({ ...c, max_caixas: v }))}
        dica="0 = sem limite."
      />
      <label className="text-xs text-tinta-fraca">
        Situação
        <select
          value={campos.status}
          onChange={(e) => setCampos((c) => ({ ...c, status: e.target.value as Situacao }))}
          className="mt-1 w-full rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
        >
          <option value="ativo">ativo</option>
          <option value="suspenso">suspenso</option>
          <option value="removido">removido</option>
        </select>
        {/* Não é rótulo: as visões que o Postfix e o Dovecot leem filtram por
            status do domínio. Suspender PARA a entrega, na hora. */}
        <span className="mt-1 block">
          Suspender INTERROMPE a entrega de todas as caixas — não é só um rótulo.
        </span>
      </label>

      {/* SENHA SIMPLES — 03/11/2026, pedido do Jhou para clientes de mais
          idade que não conseguem digitar 12 caracteres. A API exige revenda
          ou acima para salvar (é a mesma rota da cota); o admin do domínio
          leva 403. O texto diz o preço, porque quem liga é quem responde. */}
      <label className="flex items-start gap-2 text-sm text-tinta sm:col-span-4">
        <input
          type="checkbox"
          checked={campos.senha_simples}
          onChange={(e) => setCampos((c) => ({ ...c, senha_simples: e.target.checked }))}
          className="mt-1"
        />
        <span>
          Permitir senha simples (mínimo de 4 caracteres) nas caixas deste domínio
          <span className="mt-0.5 block text-xs text-tinta-fraca">
            Para quem não consegue digitar uma senha longa. Senha curta é mais fácil
            de adivinhar, e uma caixa invadida manda spam pelo nosso servidor. Vale
            para a próxima senha de cada caixa: desligar não troca as que já existem.
          </span>
        </span>
      </label>

      <div className="flex items-center gap-2 sm:col-span-4">
        <Botao tipo="submit" icone="CheckCircle2" ocupado={salvar.isPending}>
          Salvar
        </Botao>
        <Botao tom="neutro" aoClicar={aoFechar}>
          Cancelar
        </Botao>
        {salvar.error && (
          <p className="text-sm text-perigo">
            {salvar.error instanceof ErroDaAPI
              ? (Object.values(salvar.error.porCampo)[0]?.[0] ?? salvar.error.mensagem)
              : explicar(salvar.error)}
          </p>
        )}
      </div>
    </form>
  );
}

function CampoDoDominio({
  rotulo,
  valor,
  aoMudar,
  dica,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  dica: string;
}) {
  return (
    <label className="text-xs text-tinta-fraca">
      {rotulo}
      <input
        type="number"
        min={0}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        className="mt-1 w-full rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
      />
      <span className="mt-1 block">{dica}</span>
    </label>
  );
}

/** Bytes → GB para o campo. Aqui 0 é 0: o domínio não herda de ninguém. */
function emGB(bytes?: number | null): string {
  if (!bytes) return "0";
  return String(Math.round((bytes / 1024 ** 3) * 100) / 100);
}

function emBytes(gb: string): number {
  const n = Number.parseFloat(gb.replace(",", "."));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 1024 ** 3) : 0;
}

function inteiro(v: string): number {
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Uma ação de linha da tabela.
 *
 * Existe para que as quatro ações do domínio tenham o MESMO alvo de toque,
 * o mesmo foco visível e a mesma altura. Escritas à mão, elas divergiram:
 * duas tinham `whitespace-nowrap` e duas não, então "conferir DNS" quebrava
 * em duas linhas em tela estreita e empurrava a linha inteira.
 */
function AcaoDaLinha({
  children,
  aoClicar,
  tom = "acao",
  expandido,
  rotulo,
}: {
  children: ReactNode;
  aoClicar: () => void;
  tom?: "acao" | "perigo";
  /** Presente quando o botão abre uma gaveta: vira `aria-expanded`. */
  expandido?: boolean;
  /** O nome acessível, que diz O QUE e DE QUEM — "fechar" sozinho não diz. */
  rotulo: string;
}) {
  const cor =
    tom === "perigo"
      ? "border-perigo/30 text-perigo hover:bg-perigo/10"
      : "border-linha text-acao hover:bg-acao-clara";
  return (
    <button
      type="button"
      onClick={aoClicar}
      aria-label={rotulo}
      title={rotulo}
      {...(expandido === undefined ? {} : { "aria-expanded": expandido })}
      className={`min-h-[30px] whitespace-nowrap rounded-full border px-2.5 py-1 text-xs focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-acao ${cor}`}
    >
      {children}
    </button>
  );
}

function Marca({ situacao, opcional }: { situacao?: string; opcional?: boolean }) {
  if (!situacao) return <span className="text-tinta-fraca">…</span>;
  if (situacao === "ok") {
    return <Icone nome="CheckCircle2" tamanho={16} className="text-acao" />;
  }
  // OPCIONAL E AUSENTE NÃO É AVISO. Um domínio que usa os nomes da
  // plataforma não precisa de `mail.dominiodele.com.br`, e pintar isso de
  // amarelo em todos os clientes novos treinaria o operador a ignorar os
  // amarelos que importam.
  //
  // `divergente` segue amarelo mesmo sendo opcional: nome que existe e
  // aponta para outro servidor não é ausência, é desvio.
  if (opcional && (situacao === "ausente" || situacao === "erro")) {
    return (
      <span className="text-tinta-fraca" title="opcional — não é problema">
        —
      </span>
    );
  }
  return <Icone nome="AlertTriangle" tamanho={16} className="text-aviso" />;
}

/** O que a bolinha quer dizer, em palavras — vai no `title` e no leitor de tela. */
function comoLer(c: { situacao?: string; opcional?: boolean }): string {
  if (c.situacao === "ok") return "publicado e batendo";
  if (c.opcional && (c.situacao === "ausente" || c.situacao === "erro")) {
    return "opcional, e não está lá";
  }
  if (c.situacao === "ausente") return "falta";
  if (c.situacao === "divergente") return "aponta para outro lugar";
  return "não deu para consultar";
}

const NOMES: Record<string, string> = {
  mx: "MX — para onde o e-mail do domínio é entregue",
  spf: "SPF — quem tem permissão de enviar por este domínio",
  dkim: "DKIM — a assinatura que prova que a mensagem é sua",
  dmarc: "DMARC — o que fazer quando SPF e DKIM falham",
  ptr: "PTR — o nome reverso do IP que envia",
  host_mail: "mail.dominio — o endereço que o Outlook do cliente usa",
  host_webmail: "webmail.dominio — o endereço do webmail com o nome do cliente",
  // NÃO É DNS, e fica aqui assim mesmo: é nesta tela que se olha para saber
  // se o domínio está pronto. Ver api/interno/dns/certificado.go.
  certificado: "Certificado SSL — o cadeado que o cliente vê no navegador",
};

type Exame =
  | "mx"
  | "spf"
  | "dkim"
  | "dmarc"
  | "ptr"
  | "host_mail"
  | "host_webmail"
  | "certificado";

/** A ordem em que os exames aparecem, na linha e no detalhe.
 *
 * O certificado vem DEPOIS dos dois nomes próprios de propósito: ele só faz
 * sentido depois deles, e é exatamente nessa ordem que a pessoa resolve —
 * aponta os dois A, e aí emite. */
const EXAMES: Exame[] = [
  "mx",
  "spf",
  "dkim",
  "dmarc",
  "ptr",
  "host_mail",
  "host_webmail",
  "certificado",
];

/** Lê um dos exames sem que o TypeScript precise indexar a união. */
function exameDe(d: DiagnosticoDns | undefined, qual: Exame) {
  if (!d) return undefined;
  return {
    mx: d.mx,
    spf: d.spf,
    dkim: d.dkim,
    dmarc: d.dmarc,
    ptr: d.ptr,
    host_mail: d.host_mail,
    host_webmail: d.host_webmail,
    certificado: d.certificado,
  }[qual];
}

function Diagnostico({
  uuid,
  consulta,
}: {
  uuid: string;
  consulta: {
    isPending: boolean;
    isError: boolean;
    isFetching: boolean;
    error?: unknown;
    data?: DiagnosticoDns;
    refetch: () => unknown;
  };
}) {
  const dkim = useQuery({
    queryKey: ["p", "dkim", uuid],
    queryFn: () => dkimDoDominio(uuid),
  });

  if (consulta.isPending) return <p className="text-sm text-tinta-fraca">consultando o DNS…</p>;
  if (consulta.isError) return <p className="text-sm text-perigo">{explicar(consulta.error)}</p>;

  const d = consulta.data;
  return (
    <div className="grid gap-2">
      {/* O RESULTADO PODE TER ALGUNS MINUTOS: a tela confere todas as linhas
          ao abrir e guarda por cinco minutos. Quem acabou de publicar um
          registro precisa de um jeito de perguntar de novo AGORA. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-tinta-fraca">
          {d?.verificado_em
            ? `conferido às ${new Date(d.verificado_em).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`
            : ""}
        </p>
        <AcaoDaLinha
          aoClicar={() => void consulta.refetch()}
          rotulo={`Conferir de novo o DNS de ${d?.dominio ?? "este domínio"}`}
        >
          {consulta.isFetching ? "conferindo…" : "conferir de novo"}
        </AcaoDaLinha>
      </div>
      {EXAMES.map((k) => {
        const c = exameDe(d, k);
        const ok = c?.situacao === "ok";
        // Opcional e ausente não é pendência: é um recurso que este domínio
        // não usa. Nem borda de aviso, nem selo amarelo — senão a tela
        // mostra duas pendências permanentes em todo cliente novo, e o
        // operador aprende a não olhar.
        const soNaoUsa = !!c?.opcional && (c.situacao === "ausente" || c.situacao === "erro");
        return (
          <div
            key={k}
            className={`rounded-lg border bg-white p-3 ${
              ok || soNaoUsa ? "border-linha" : "border-aviso/50"
            }`}
          >
            <div className="flex items-start gap-2">
              <Marca situacao={c?.situacao} opcional={c?.opcional} />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-tinta">{NOMES[k]}</p>
                {/* O que está publicado HOJE, e o que deveria estar. Sem o
                    "deveria", a tela só troca uma pergunta por outra. */}
                {c?.encontrado && (
                  <p className="mt-1 break-all font-mono text-xs text-tinta-fraca">
                    encontrado: {c.encontrado}
                  </p>
                )}
                {/* "deveria ser" só quando há algo a corrigir. Para um
                    recurso que o domínio simplesmente não usa, isso leria
                    como cobrança. */}
                {!ok && !soNaoUsa && c?.esperado && (
                  <p className="mt-1 break-all font-mono text-xs text-tinta">
                    deveria ser: {c.esperado}
                  </p>
                )}
                {soNaoUsa && c?.esperado && (
                  <p className="mt-1 break-all font-mono text-xs text-tinta-fraca">
                    se quiser usar: aponte um registro A para {c.esperado}
                  </p>
                )}
                {c?.explicacao && <p className="mt-1 text-xs text-tinta-fraca">{c.explicacao}</p>}
              </div>
              <Selo
                texto={ok ? "ok" : soNaoUsa ? "não usa" : (c?.situacao ?? "?")}
                tom={ok ? "ok" : soNaoUsa ? "neutro" : "aviso"}
              />
            </div>
          </div>
        );
      })}

      {dkim.data && (
        <div className="rounded-lg border border-linha bg-white p-3">
          <p className="text-sm text-tinta">Registro DKIM para publicar</p>
          {dkim.data.valor ? (
            <>
              <p className="mt-2 text-xs text-tinta-fraca">
                nome: <span className="font-mono">{dkim.data.nome}</span> · tipo TXT
              </p>
              <p className="mt-1 select-all break-all rounded border border-linha bg-fundo p-2 font-mono text-xs">
                {dkim.data.valor}
              </p>
            </>
          ) : (
            <p className="mt-1 text-xs text-tinta-fraca">
              {dkim.data.aviso ?? "a chave ainda não foi gerada"} — ela nasce sozinha
              em até cinco minutos depois de o domínio ser criado.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * ANTISPAM DO DOMÍNIO: limite próprio e listas de remetente.
 *
 * REVENDA PARA CIMA. O botão não aparece para quem não pode, e isso não é a
 * proteção — a proteção é a rota recusar. Mostrar um botão que sempre dá 403
 * seria só uma forma ruim de informar.
 *
 * O QUE A TELA PRECISA DIZER, e que o dado sozinho não diz:
 *
 *   1. Vazio é "usa o padrão", não zero. Zero na quarentena mandaria TODA
 *      mensagem do domínio para a pasta Junk.
 *   2. Subir o limite é uma TROCA, não uma melhora.
 *   3. A recusa é irreversível; a quarentena não.
 *   4. Liberar um endereço libera quem o FORJAR.
 *   5. Nada disso vale na hora — são até cinco minutos.
 *
 * Cada um desses é um chamado que a frase evita.
 */
function AntispamDoDominio({ uuid }: { uuid: string }) {
  const fila = useQueryClient();
  const [erro, setErro] = useState("");
  const [quarentena, setQuarentena] = useState("");
  const [recusa, setRecusa] = useState("");
  const [novo, setNovo] = useState({ tipo: "branca", remetente: "", nota: "" });

  const consulta = useQuery({
    queryKey: ["p", "antispam", uuid],
    queryFn: () => dominios.antispam(uuid),
  });

  // O formulário começa com o que está gravado. Sem isto, abrir e salvar
  // sem tocar em nada apagaria a configuração existente.
  useEffect(() => {
    if (!consulta.data) return;
    setQuarentena(consulta.data.quarentena == null ? "" : String(consulta.data.quarentena));
    setRecusa(consulta.data.recusa == null ? "" : String(consulta.data.recusa));
  }, [consulta.data]);

  const atualizar = () => void fila.invalidateQueries({ queryKey: ["p", "antispam", uuid] });

  const salvar = useMutation({
    // Campo em branco vira `null`, que é "volta ao padrão" — e NÃO zero.
    mutationFn: () =>
      dominios.definirAntispam(uuid, {
        quarentena: quarentena.trim() === "" ? null : Number(quarentena),
        recusa: recusa.trim() === "" ? null : Number(recusa),
      }),
    onSuccess: () => {
      setErro("");
      atualizar();
    },
    onError: (e) => setErro(explicar(e)),
  });

  const incluir = useMutation({
    mutationFn: () =>
      dominios.acrescentarNaLista(uuid, {
        tipo: novo.tipo,
        remetente: novo.remetente.trim(),
        nota: novo.nota.trim() || undefined,
      }),
    onSuccess: () => {
      setErro("");
      setNovo({ tipo: "branca", remetente: "", nota: "" });
      atualizar();
    },
    onError: (e) => setErro(explicar(e)),
  });

  const remover = useMutation({
    mutationFn: (entrada: string) => dominios.removerDaLista(uuid, entrada),
    onSuccess: atualizar,
    onError: (e) => setErro(explicar(e)),
  });

  if (consulta.isPending) return <p className="text-sm text-tinta-fraca">carregando…</p>;
  if (consulta.isError) return <p className="text-sm text-perigo">{explicar(consulta.error)}</p>;

  const d = consulta.data!;
  const listas = d.listas ?? [];

  return (
    <div className="grid gap-4">
      <div className="rounded-lg border border-linha bg-white p-3">
        <p className="text-sm text-tinta">Limite de spam deste domínio</p>
        <p className="mt-1 text-xs text-tinta-fraca">
          Em branco usa o padrão da plataforma: quarentena {d.quarentena_padrao}, recusa{" "}
          {d.recusa_padrao}. <strong className="text-tinta">Branco não é zero</strong> — zero
          na quarentena mandaria todo e-mail do domínio para a pasta Spam.
        </p>

        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-xs text-tinta-fraca">
            Quarentena (vai para a pasta Spam)
            <input
              value={quarentena}
              onChange={(e) => setQuarentena(e.target.value)}
              inputMode="decimal"
              placeholder={String(d.quarentena_padrao)}
              className="mt-1 block w-28 rounded-lg border border-linha px-2 py-1 text-sm"
            />
          </label>
          <label className="text-xs text-tinta-fraca">
            Recusa (nem chega a entrar)
            <input
              value={recusa}
              onChange={(e) => setRecusa(e.target.value)}
              inputMode="decimal"
              placeholder={String(d.recusa_padrao)}
              className="mt-1 block w-28 rounded-lg border border-linha px-2 py-1 text-sm"
            />
          </label>
          <button
            onClick={() => salvar.mutate()}
            disabled={salvar.isPending}
            className="rounded-full bg-acao px-4 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {salvar.isPending ? "salvando…" : "salvar"}
          </button>
        </div>

        {/* A TROCA, dita antes de alguém fazer. */}
        <p className="mt-3 border-l-2 border-aviso pl-3 text-xs text-tinta-media">
          Subir o limite é uma troca: menos e-mail legítimo parando na quarentena, e{" "}
          <strong className="text-tinta">mais spam na caixa de entrada</strong>. E a{" "}
          <strong className="text-tinta">recusa é irreversível</strong> — acima dela o
          remetente recebe erro e não tem para onde recorrer. Na quarentena o erro se desfaz:
          a mensagem está na pasta, e o dono tira de lá.
        </p>
      </div>

      <div className="rounded-lg border border-linha bg-white p-3">
        <p className="text-sm text-tinta">Remetentes sempre liberados ou sempre recusados</p>
        <p className="mt-1 text-xs text-tinta-fraca">
          Um endereço (<span className="font-mono">fulano@empresa.com.br</span>) ou um domínio
          inteiro (<span className="font-mono">@empresa.com.br</span>). Vale{" "}
          <strong className="text-tinta">só neste domínio</strong>.
        </p>

        <div className="mt-3 flex flex-wrap items-end gap-2">
          <select
            value={novo.tipo}
            onChange={(e) => setNovo((n) => ({ ...n, tipo: e.target.value }))}
            className="rounded-lg border border-linha px-2 py-1 text-sm"
          >
            <option value="branca">sempre liberar</option>
            <option value="preta">sempre recusar</option>
          </select>
          <input
            value={novo.remetente}
            onChange={(e) => setNovo((n) => ({ ...n, remetente: e.target.value }))}
            placeholder="fulano@empresa.com.br"
            className="min-w-[220px] flex-1 rounded-lg border border-linha px-2 py-1 text-sm"
          />
          <input
            value={novo.nota}
            onChange={(e) => setNovo((n) => ({ ...n, nota: e.target.value }))}
            placeholder="por quê (opcional)"
            className="min-w-[160px] rounded-lg border border-linha px-2 py-1 text-sm"
          />
          <button
            onClick={() => incluir.mutate()}
            disabled={incluir.isPending || !novo.remetente.trim()}
            className="rounded-full bg-acao px-4 py-1.5 text-sm text-white disabled:opacity-40"
          >
            incluir
          </button>
        </div>

        {/* O QUE LIBERAR SIGNIFICA DE VERDADE. */}
        <p className="mt-3 border-l-2 border-aviso pl-3 text-xs text-tinta-media">
          Liberar um endereço libera também{" "}
          <strong className="text-tinta">quem o forjar</strong> — e forjar remetente é fácil
          em domínio sem DKIM e DMARC. Libere endereço de quem você conhece, e prefira o
          endereço exato ao domínio inteiro.
        </p>

        {listas.length > 0 && (
          <ul className="mt-3 grid gap-1">
            {listas.map((l) => (
              <li
                key={l.uuid}
                className="flex flex-wrap items-center gap-2 rounded border border-linha px-2 py-1 text-xs"
              >
                <Selo
                  texto={l.tipo === "branca" ? "liberado" : "recusado"}
                  tom={l.tipo === "branca" ? "ok" : "erro"}
                />
                <span className="font-mono text-tinta">{l.remetente}</span>
                {l.nota && <span className="text-tinta-fraca">— {l.nota}</span>}
                <span className="ml-auto text-tinta-fraca">{l.criado_por}</span>
                <button
                  onClick={() => remover.mutate(l.uuid!)}
                  disabled={remover.isPending}
                  className="text-perigo underline disabled:opacity-50"
                >
                  tirar
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* QUANDO VALE. "Mudei e não mudou nada" é o chamado que esta linha evita. */}
      <p className="text-xs text-tinta-fraca">
        As mudanças valem em <strong className="text-tinta">até cinco minutos</strong>, quando
        o antispam relê a configuração.
      </p>

      {erro && <p className="text-sm text-perigo">{erro}</p>}
    </div>
  );
}
